<template>
  <div class="investments-page notranslate">
    <banner nav-public></banner>
    <main>
      <header class="split container">
        <div id="header-content">
          <h2 v-t>Now every Canadian can access investments that are far from everyday.</h2>
        </div>
        <picture v-if="languageSelected === 'french'">
          <source :srcset="require('../images/investments/hero-mobile-fr.png')" media="(max-width: 47.99rem)">
          <source :srcset="require('../images/investments/hero-desktop-fr.png')" media="(min-width: 48rem)">
          <img width="768" src="../images/investments/hero-desktop-fr.png" alt="téléphone mobile, des lunettes et un stylo" class="hero-images">
        </picture>
        <picture v-else>
          <source :srcset="require('../images/investments/hero-mobile.png')" media="(max-width: 47.99rem)">
          <source :srcset="require('../images/investments/hero-desktop.png')" media="(min-width: 48rem)">
          <img width="768" src="../images/investments/hero-desktop.png" alt="mobile phone, glasses and a pen" class="hero-images">
        </picture>
      </header>
      <section class="investment-philosophy container">
        <div class="investment-philosophy-content">
          <p class="section-title"><strong v-t>Our Investment Philosophy</strong></p>
          <h3 v-t>Diversification + cash flow = lower volatility</h3>
          <p v-t>We take a low-risk, long-term approach to investing. Managing volatility and focusing on cashflow is how you get to a comfortable retirement.</p>
          <div class="reason">
            <img class="bullet" src="../images/investments/diversification-icon.svg" :alt="starIconAlt" />
            <div>
              <h5 v-t>Broad diversification</h5>
              <p v-t>By spreading your money across more investments, you’ll take advantage of more opportunities. And, when the market drops (and it will sometimes over the long term), your money is better protected.</p>
            </div>
          </div>
          <div class="reason">
            <img class="bullet" src="../images/investments/cashflow-icon.svg" :alt="cashflowIconAlt" />
            <div>
              <h5 v-t>Cash flow generating investments</h5>
              <p v-t>A consistent stream of cash flows means you can enjoy the steady returns and income without having to worry a lot about the ups and downs of the value of your portfolio.</p>
            </div>
          </div>
        </div>
        <div class="plant-diagram">
          <video
            autoplay
            loop
            :src="require(`../images/investments/plant-diagram.mp4`)"
            preload="auto"
            muted
            playsinline>
          </video>
          <div>
            <p class="small" v-t>Mortgages</p>
            <p class="small" v-t>Real Estate</p >
            <p class="small" v-t>Fixed Income</p>
          </div>
        </div>
      </section>
      <section class="money-tree container">
        <div class="money-tree-content" >
          <p class="section-title"><strong v-t>What You Get</strong></p>
          <h3 v-t>All-weather portfolios designed to perform rain or shine.</h3>
          <p v-t>Our portfolios are professionally diversified to help protect them from the ups and downs of the markets. So you can feel confident that your investments are working to achieve your goals ⁠— regardless of the weather.</p>
        </div>
        <figure class="money-tree-img">
          <img src="../images/investments/money-tree.jpg" alt="money tree illustration">
        </figure>
      </section>

      <cta></cta>
    </main>
    <footer-component @language-changed="languageChanged"></footer-component>
  </div>
</template>

<script>
  import Banner from '@layoutTheme/banner';
  import Footer from '@layoutTheme/footer';
  import cta from '~vue/components/cta';

  export default {

    data() {
      return {
        languageSelected: '',
      };
    },
    components: {
      Banner,
      'footer-component': Footer,
      cta,

    },
    methods: {
      languageChanged(language) {
        this.languageSelected = language;
      },
    },
    computed: {
      starIconAlt() {
        return this.$t("star icon")
      },
      cashflowIconAlt() {
        return this.$t("cashflow icon")
      },
    },
  };
</script>

<style lang="scss" scoped>
  sup a {
    text-decoration: none;
  }

  #header-content {
    text-align: left;
    @media #{$screen-width-medium} { max-width: 33rem; }
  }

  .hero-images {
    margin: 0 auto;
    max-width: 24rem;
    @media #{$screen-width-medium} {
      max-width: none;
    }

    @media #{$screen-width-large} {
      margin-right: -6rem;
    }
  }

  .section-description {
    max-width: 100%;
    @media #{$screen-width-medium} {
      width: 21rem;
    }
    @media #{$screen-width-large} {
      width: 100%;
    }
  }

  .section-title {
    strong {
      display: inline-block;
      transition: all 1s ease;
      color: $secondary-500;
      padding-bottom: 1rem;
      border-bottom: 1px solid $primary-300;
    }
  }

  .investment-philosophy {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    margin-top: 2rem;

    @media #{$screen-width-large} {
      flex-direction: row;
    }

    .investment-philosophy-content {
      @media #{$screen-width-medium} {
        padding: 0 5rem;
        margin-left: 2rem;
      }

      @media #{$screen-width-large} {
        order: 2;
        max-width: 35rem;
        padding: 0;
      }
    }
  }

  .plant-diagram {
    display: flex;
    flex-direction: column;
    flex: 2 1 auto;
    @media #{$screen-width-large} {
      margin: 0 0 0 -1rem;
      top: 2rem;
    }

    video {
      height: 366px;
      @media #{$screen-width-medium} { height: 442px; }
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
     /* IE10+ CSS styles go here */
      video { width: 442px; }
    }

    div {
      display: flex;
      justify-content: space-around;
      top: -3rem;
      width: 440px;
      margin: 0 auto;

      @media #{$screen-width-medium} {
        justify-content: space-between;
        width: 442px;
      }
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
     /* IE10+ CSS styles go here */
      div {
        justify-content: space-between;
        margin: 0;

      }
    }
  }

  .money-tree {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin-bottom: 2rem;

    @media #{$screen-width-medium} {
      flex-direction: row;
      padding-top: 2rem;
    }

    .money-tree-img {
      margin: 2rem -9rem 0 -14rem;
      z-index: -1;
      @media #{$screen-width-medium} { margin: 0 -12rem -2rem -10rem; }

      @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
     /* IE10+ CSS styles go here */
        margin: 0 -42rem 12rem;
      }
    }

    .money-tree-content {
      display: flex;
      flex-direction: column;
      max-width: 64rem;
      @media #{$screen-width-medium} { min-width: 21rem; }
    }
  }

  .portfolios-list {
    @media #{$screen-width-medium} {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;
    }

    li { margin-bottom: 0; }
  }

  .reason {
    display: flex;

    img {
      align-self: flex-start;
      margin-right: 1rem;
      margin-top: 1rem;
      height: 2.5rem;
      width: 2.5rem;
    }
  }

  .portfolio-headline {
    display: flex;
    flex-direction: column;
    align-items: left;
    padding-bottom: 2rem;
    padding-top: 2rem;

    @media #{$screen-width-medium} {
      align-items: center;
    }

    p {
      @media #{$screen-width-medium} {
        width: 35rem;
        text-align: center;
      }
    }
  }

  .portfolio-cards {
    @media #{$screen-width-medium} {
      display: flex;
      justify-content: space-evenly;
    }

    @media #{$screen-width-large} {
      justify-content: center;
    }
  }

  .portfolio-card > a.portfolio-card-link {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    padding: 2rem;
  }

  .portfolio-card {
    border-radius: 5px;
    border: 1px $primary-100 solid;
    margin: 1rem 0;
    cursor: pointer;
    box-shadow: 0 4px 8px 0 rgba(134,134,143,0.2);
    position: relative;
    min-height: 20rem;
    max-width: 50%;
    flex: 1 1 auto;

    &:hover {
      background-color: #f7f7f8;
      span.button {  background: $primary-900; }
      span.button.alt {  background: $secondary-700; }
    }

    svg {
      max-width: 3rem;
      max-height: 3rem;
    }


    div { padding-top: 1rem; }

    a.portfolio-card-link,
    a.portfolio-card-link:visited,
    a.portfolio-card-link:hover {
      text-decoration: none;
      color: $neutral-900;
    }
  }

  .portfolio-card:nth-of-type(even) {
    @media #{$screen-width-medium} {
      margin-left: 1rem;
    }

    border: 1px $secondary-100 solid;
  }

  /* Float - animation */
  .hvr-float {
    -webkit-transition: all 0.3s ease-out;
    transition: all 0.3s ease-out;
    -webkit-transition-property: transform;
    transition-property: transform;
  }

  .hvr-float:hover, .hvr-float:focus, .hvr-float:active {
    -webkit-transform: translateY(-2px);
    transform: translateY(-2px);
  }
</style>
